import type { ApiError } from '@/lib/api-client';

/**
 * P5-4 Commit A — types for the book feature module.
 *
 * Shape mirrors the backend `PreviewEnvelope` returned by
 * `GET /api/book/orders/:orderId` (implemented at
 * `src/book/preview-read/preview-read.controller.ts:25-30` + service at
 * `src/book/preview-read/preview-read.service.ts:143-149`).
 *
 * Backend + PWA are two separate npm projects; Prisma types cannot
 * cross the boundary. The `BookOrderStatus` union below is the
 * string-literal projection of the Prisma enum declared at
 * `prisma/schema.prisma:344-368` (23 values). Keep in lockstep — a
 * new backend status appears here as a new union member.
 */

/**
 * BookOrderStatus — mirrors `prisma/schema.prisma:344-368`.
 *
 * ═══ COORDINATION NOTE — `photo_rejected` LANDS AHEAD OF THE BACKEND
 *     MIGRATION 2026-08-06 (Sebastian ruling) ═══════════════════════
 *
 * The sister session has a `photo_rejected` status built and a
 * migration ready. The PWA must land the union value FIRST because a
 * TypeScript union tolerates a value the runtime enum does not carry
 * yet — but a backend that starts writing `photo_rejected` cannot
 * tolerate a PWA that does not know the value (the router's default
 * branch shows the loading placeholder forever). Landing here first
 * unblocks the migration.
 *
 * `photo_rejected` fires when a customer photo is rejected because
 * more than one face is on it. Marker = Personen (customer sent back
 * to Schritt 1's photo). Rejection detail lives at
 * `envelope.intake.derived.rejection` (see EnvelopeRejection below).
 */
export type BookOrderStatus =
  | 'draft_configuring'
  | 'light_gate_pending'
  | 'summary_review'
  | 'anchor_pending'
  | 'anchor_review'
  | 'anchor_approved'
  | 'story_generating'
  | 'text_editing'
  | 'preview_rendering'
  | 'preview_ready'
  // 6-1 payment surface (Sebastian ruling 2026-08-07) — resting place
  // between `preview_ready` and `paid` for the entire checkout: sales-
  // unit format choice AND the Stripe round trip. Re-entry after
  // Stripe abandonment lands here; `BestellungRoute` renders on this
  // status (mirror of the Prisma enum at `prisma/schema.prisma:395`).
  // Closes tracker [pwa-status-to-step-add-checkout-pending] filed
  // with 6-1; PWA union stale since 2026-08-07 because the 19-day
  // Cloudflare red-build window hid the drift until 2026-09-07.
  | 'checkout_pending'
  | 'paid'
  | 'rendering_full'
  | 'qa_running'
  | 'qa_regen'
  | 'qa_human_queue'
  | 'customer_review'
  | 'print_approved'
  | 'prepress'
  | 'handoff_pod'
  | 'shipped'
  | 'delivered'
  | 'paused_billing'
  | 'reclamation_open'
  | 'cancelled'
  | 'photo_rejected'
  // Sebastian ruling 2026-09-08 evening (Commit 2 of A+B+C) —
  // release-chain terminal-fail state. Backend flips
  // anchor_pending → book_release_chain_error when
  // BookReleaseChainService.run exhausts its BullMQ attempts (see
  // `src/book/summary-review/release-chain.processor.ts`). PWA
  // renders BookStatusInfoScreen with the released "es geht nichts
  // verloren" copy (STRINGS.book.status.releaseChainError). Not
  // customer-actionable — customer waits for the resume mail
  // (Promise 2, Commit 3). Included in NON_TERMINAL_POLLING_STATES
  // so an operator recovery flip lands on the customer's screen
  // without a manual refresh.
  | 'book_release_chain_error'
  // Sebastian ruling 2026-09-10 (Paket 3) — image-refusal state.
  // Backend flips anchor_pending → image_refused when the OpenAI
  // image endpoint returns a 2xx-with-error content refusal on the
  // book_anchor render (`RenderContentRefusalError`, caught + wrapped
  // as UnrecoverableError in the anchor render processor). Distinct
  // from photo_rejected (deterministic Google-Vision rejection with a
  // specific castKey to swap) — image_refused is opaque and the
  // customer-facing copy is stage-dependent, driven by
  // `envelope.intake.derived.imageRefusal.stage`. Only book_anchor
  // stage fires today; cover + scene stages are forward-compat for
  // when the release-time moderation Sammellauf lands.
  | 'image_refused'
  // 2026-10-14 — per-figure block-derivation rejection state. Backend
  // flips light_gate_pending → block_derivation_rejected when one or
  // more cast members' block derivation fails after retries (vehicle
  // entered as `tier`, person photo not derivable). PWA renders the
  // Personen screen with a hint on each failed figure; the hint comes
  // from `envelope.intake.derived.rejections` (plural, keyed by cast
  // key) with reason='not_an_animal' | 'photo_unusable'. The customer
  // fixes the figure with the existing swap / remove UI; the
  // resubmit re-enters derivation via light_gate_pending.
  | 'block_derivation_rejected';

/**
 * CustomerRenderState — mirrors the backend four-value union at
 * `src/book/preview-read/render-state.helper.ts:72`.
 *
 * Sebastian ruling 2026-08-26: `'blocked'` added. Produced when
 * `BookOrder.status === 'preview_rendering'` AND the scene has no
 * `assembled_prompt.bodyHash` on `Scene.data`. The assembly post-hook
 * never wrote a prompt for this scene, no render will ever fire.
 * PWA shows a static tile + "Diese Seite konnten wir nicht zeichnen.
 * Melde dich bei uns, dann kümmern wir uns darum." No spinner, no
 * retry affordance (none exists yet).
 */
export type CustomerRenderState = 'rendering' | 'ready' | 'retry_available' | 'blocked';

/**
 * PanelBoxEnvelope — Amendment v2.0.9 §AF9-2 Etappe E surface
 * mirror. Kept ONLY for the cover cartouche (Phase 5 I5 scope); the
 * per-page path retired the panel-in-envelope surface on 2026-08-22
 * when the page image started shipping composed (text + panel baked
 * in). Normalised [0, 1] rectangle plus a `source` field so the
 * browser network tab makes the fallback-fire rate visible.
 */
export interface PanelBoxEnvelope {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  /**
   * `'customer'` added Position-1 Commit-3 (Sebastian ruling
   * 2026-09-03) for the move-panel option. The picker reports
   * `'customer'` when the RenderAttempt carries the customer-set
   * coords; the PWA reads this to know whether the panel is where
   * the vision reader placed it or where the customer dragged it.
   */
  readonly source: 'detected' | 'fallback' | 'fixed' | 'customer';
}

/**
 * PreviewPage — mirrors backend `PreviewPage` in
 * `src/book/preview-read/preview-read.service.ts`.
 *
 * 2026-08-22 wiring commit: the raw `imageUrl` + `textZone` + `panelBox`
 * trio was collapsed to a single `composedImageUrl`. The signed URL
 * points at the screen-composed page image — text + panel are baked
 * into the pixels, no overlay math on the client. `null` means the
 * compose service failed for this scene (logged with
 * `[compose-on-demand-failed]` server-side); the PWA falls through
 * to the "rendering" placeholder.
 */
export interface PreviewPage {
  readonly pageNo: number;
  readonly sceneId: string;
  readonly text: string;
  readonly composedImageUrl: string | null;
  readonly renderState: CustomerRenderState;
  readonly remainingPage: number;
  /**
   * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — symmetric
   * with `PreviewCoverRegen.used`. Sourced from the same server-side
   * count that feeds `remainingPage`; a scalar surface of a value
   * already computed. `usedPage > 0` is the pick-menu-entry visibility
   * gate: post QA-removal every customer_regen attempt reaches
   * accepted, so `usedPage > 0` ↔ "more than one image exists for this
   * scene" exactly. `remainingPage` is the cap-facing field — do NOT
   * gate menu visibility on it (a customer at the cap has three images
   * and must still be able to pick).
   */
  readonly usedPage: number;
  /**
   * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — the
   * customer's current-image override, mirrors backend
   * `Scene.selectedRenderAttemptId`. NULL = newest wins (default);
   * non-NULL = this specific RenderAttempt is the active image.
   * Consumed by the picker dialog to render the "Aktuell" badge.
   */
  readonly selectedRenderAttemptId: string | null;
  /**
   * Position-1 Commit-2 (Sebastian ruling 2026-09-04) — server-computed
   * character cap for the customer text-edit dialog. Mirrors backend
   * `PreviewPage.maxCharsForPanel`. Populated only when
   * `envelope.status === 'customer_review'`; `null` on every other
   * status AND at the customer_review boundary when the page has no
   * picked RenderAttempt yet. Per Sebastian's null-behaviour ruling:
   * hide the counter, endpoint falls back to 150 chars server-side.
   */
  readonly maxCharsForPanel: number | null;
  /**
   * Position-1 Commit-3 (Sebastian ruling 2026-09-03) — RAW picker
   * output for the diagnostic surface (source visibility in the
   * browser's network tab). NOT rendered as the drag-surface outline
   * — the composer draws a text-derived rectangle that differs on
   * the height axis. Overlay renders `drawnPanel` (below) instead.
   */
  readonly panelBox: PanelBoxEnvelope | null;
  /**
   * Position-1 Commit-3 addendum (Sebastian ruling 2026-09-03 evening)
   * — the geometry `drawSceneText` actually draws for this page, in
   * doc fractions [0,1]. Server-computed via the composer's own
   * kernel (`panelGeomForDetectedBox`) so the drag surface outline
   * matches the panel the customer will see at print. `null` when
   * (a) no picked box OR (b) composer's fit gate returns 'does-not-fit'
   * for this page (zone-fallback — drag surface not meaningful).
   * Client uses this null to suppress option 2.
   */
  readonly drawnPanel: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
    readonly writableWidth: number;
  } | null;
  /**
   * Position-1 Commit-3 — signed URL for the RAW picked scene image
   * (no text panel overlay). Populated only at `customer_review`
   * AND when the raw-URL sign call succeeded. The drag surface
   * renders this as its background.
   */
  readonly rawImageUrl: string | null;
  /**
   * Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — mirrors
   * backend `PreviewPage.retryExhausted`. `true` iff the customer has
   * spent all three customer_regen attempts on this scene AND none has
   * been accepted / pass_flagged. The failed-tile UI on the review
   * screen replaces the "Nochmal zeichnen" button with the released
   * cap-exhausted line (`STRINGS.book.review.failureState
   * .capExhaustedLine`); the server refuses further regen requests
   * with `scene_retry_exhausted` (409). `false` on every scene that
   * has fewer than 3 attempts OR at least one accepted attempt.
   */
  readonly retryExhausted: boolean;
  /**
   * Sebastian correction slice 2026-09-10 (Part 3.3 parity) — `true`
   * when the latest scene RenderAttempt's `errorClass ===
   * 'RenderContentRefusalError'`. Kept for parity with
   * `PreviewCover.contentRefused` even though the scene failure
   * branch (Part 3.1) does not currently branch on cause. Optional
   * for backward compat with pre-slice envelopes.
   */
  readonly contentRefused?: boolean;
}

/**
 * PreviewTile — mirrors backend `PreviewTile` in
 * `src/book/preview-read/preview-read.service.ts`.
 *
 * Sebastian ruling 2026-08-12 (three-state). The preview strip shows
 * exactly `k` tiles for distributed preview pageNos (picked by
 * `pickPreviewPageNos(N, k)` — for a standard 20-page book with k=4,
 * pageNos are [1, 7, 13, 19]). Each tile carries a state:
 *
 *   - `rendering` — render in flight. PWA shows spinner +
 *                   `STRINGS.book.preview.tileRendering`.
 *   - `ready`     — image proven. PWA shows the image.
 *   - `failed`    — render failed final / error. PWA shows static
 *                   placeholder + `STRINGS.book.preview.tileFailed`.
 *                   No spinner (motion would lie).
 *   - `blocked`   — assembly never produced a prompt for this scene
 *                   during preview_rendering; no render will ever
 *                   fire. PWA shows static placeholder +
 *                   `STRINGS.book.preview.tileBlocked` verbatim. No
 *                   spinner. No retry affordance (none exists yet).
 *                   Sebastian ruling 2026-08-26.
 *
 * `composedImageUrl` is non-null iff `state === 'ready'` (2026-08-22
 * wiring commit — raw `imageUrl` + `panelBox` collapsed into the
 * composed image). `szeneLetter` is positional (A, B, C, D for k=4).
 * Label composes as `Szene {szeneLetter} · im Buch Seite {pageNo}`.
 */
export type PreviewTileState = 'rendering' | 'ready' | 'failed' | 'blocked';

export interface PreviewTile {
  readonly pageNo: number;
  readonly szeneLetter: string;
  readonly state: PreviewTileState;
  /**
   * 2026-08-22 wiring commit — same rename as `PreviewPage`. The
   * screen-composed tile image (text + panel baked in). Non-null
   * iff `state === 'ready'`.
   */
  readonly composedImageUrl: string | null;
  /**
   * Sebastian ruling 2026-09-01 — signed URL for the RAW picked
   * scene image (no text panel). Mirrors backend
   * `PreviewTile.rawImageUrl`. The four preview tiles read this
   * field so the customer sees "how the figures look" without the
   * printed-page text panel obscuring the illustration; a companion
   * copy line above the tiles explains that the finished book will
   * carry the scene text in a light panel. Non-null iff
   * `state === 'ready'` AND the backend raw-URL sign call itself
   * succeeded. When null the tile falls through to the same
   * placeholder as when `composedImageUrl` is null. `composedImageUrl`
   * is kept unchanged for any consumer that still wants the panelled
   * image (post-payment full-book view, share path, email).
   */
  readonly rawImageUrl: string | null;
}

/**
 * PreviewCover — mirrors backend `PreviewCover` in
 * `src/book/preview-read/preview-read.service.ts`.
 *
 * Five fields since 2026-08-05 (envelope-anchor-gate commit):
 *  - `text` — cover overlay text from storyText.cover.
 *  - `imageUrl` — signed URL for the latest book_anchor attempt when
 *    that attempt's status ∈ {accepted, pending} AND its storage
 *    location is non-null; null otherwise. **Meaning widened in this
 *    commit**: pre-widening the field read "accepted only"; post-
 *    widening it also carries the pending anchor-review case so the
 *    Schritt-2 UI can render the image the customer is deciding on.
 *  - `imageIsAccepted` — TRUE iff the signed `imageUrl` came from an
 *    ACCEPTED attempt. FALSE when null OR when it came from a pending
 *    attempt. A consumer that must not present an unaccepted image
 *    reads this field; before the widening the same information was
 *    implicit in the "accepted only" semantics.
 *  - `attemptId` — AnchorAttempt.id the client passes to the
 *    accept/regen endpoints. Null when no book_anchor exists yet OR
 *    no attempts. When present it is the max-attemptNo attempt
 *    regardless of status; accept/regen re-verify via
 *    `anchor_attempt_stale`.
 *  - `remainingAnchor` — `anchor_regen_cap - regensUsed`. Values
 *    0..cap. Bau-Spez v3.1 §6 Schritt 2 states derive from this alone
 *    when status === 'anchor_review': 2 = state 2 (erster Anblick),
 *    1 = state 3 (nach dem ersten), 0 = state 4 (cap reached; button
 *    disappears, "Zurück zu den Personen" outline replaces it).
 *  - `panelBox` — envelope surface preserved (mirror of backend
 *    `PreviewCover.panelBox`). Currently UNUSED client-side (2026-08-23
 *    overlay removal). Reserved for the future I5 cover composer,
 *    which will bake the title into the pixels of the cover image and
 *    at that point may or may not still expose this field. Do not add
 *    a client render path that consumes it — that was the dormant
 *    layer whose title-emission wake-up motivated the removal.
 */
/**
 * Cover-regen slice (Sebastian ruling 2026-09-05) — nested regen
 * object per cover slot. Mirrors backend `PreviewCoverRegen`.
 * Empty `attempts` in step 4; the pick-earlier slice populates
 * per-attempt metadata. Signed URLs are minted on-demand — not in
 * the envelope.
 *
 * DELIBERATE ASYMMETRY (Sebastian ruling 2026-09-05) — the SCENE
 * side (`PreviewPage`) still uses flat `regenRemainingPage`;
 * migrating scenes to nested today would touch four consumers for
 * no functional change. The pick-earlier slice folds both cover +
 * scenes into one nested shape. Cover-nested / scenes-flat drift
 * lasts two slices and resolves itself.
 */
export interface PreviewCoverRegen {
  readonly cap: number;
  readonly used: number;
  readonly remaining: number;
  readonly attempts: readonly [];
  /**
   * Jugendschutz launch-slice (Sebastian ruling 2026-09-10) — mirrors
   * backend `PreviewCoverRegen.retryExhausted`. `true` iff the
   * customer has fired 3 or more `customer_cover_regen` attempts on
   * the cover Anchor AND none of the cover Anchor's attempts is
   * accepted. The failed-tile UI on the front-cover slot replaces
   * BOTH "Nochmal zeichnen" and "Anderer Titel" with the cap-
   * exhausted line; the server refuses further regen requests with
   * `cover_retry_exhausted` (409).
   */
  readonly retryExhausted: boolean;
}

export interface PreviewCover {
  readonly text: string;
  readonly imageUrl: string | null;
  readonly imageIsAccepted: boolean;
  /**
   * `true` iff `imageUrl` is the composed front cover (AnchorType.cover
   * — cartouche + title painted inside), `false` when the backend
   * falls back to the raw book_anchor character portrait OR when
   * imageUrl is null. Only the book review page's "Share the cover"
   * option reads this today; existing consumers may ignore it.
   * Optional for backwards-compat with pre-feat/book-review-followup
   * envelopes and existing component-test fixtures.
   */
  readonly imageIsPrintCover?: boolean;
  readonly attemptId: string | null;
  readonly remainingAnchor: number;
  readonly panelBox: PanelBoxEnvelope | null;
  /**
   * Sebastian ruling 2026-09-16 — title-page cast list. Same
   * derivation the print processor consumes (backend
   * `deriveCastMembers(intake)`); the Freigabe screen's title-page
   * mirror renders the same names in the same order joined by
   * " · ". Empty on pre-extraction orders.
   */
  readonly castMembers: readonly {
    readonly name: string;
    readonly personType: 'person' | 'tier';
  }[];
  /**
   * Cover-regen slice (Sebastian ruling 2026-09-05) — customer-facing
   * cover render state, derived from the latest AnchorAttempt.status
   * of the cover-type Anchor row. Symmetric with
   * `PreviewPage.renderState`; the Change dialog's cover option 1
   * uses it (with `regen.remaining`) to gate the Save button.
   */
  readonly renderState: CustomerRenderState;
  readonly regen: PreviewCoverRegen;
  /**
   * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — mirror
   * of `PreviewPage.selectedRenderAttemptId` for the cover path.
   * Sourced from backend `Anchor.selectedAnchorAttemptId`. NULL =
   * newest wins; non-NULL = this specific AnchorAttempt is the active
   * cover image.
   */
  readonly selectedAnchorAttemptId: string | null;
  /**
   * Sebastian correction slice 2026-09-10 (Part 3.3) — mirrors backend
   * `PreviewCover.contentRefused`. `true` when the latest cover
   * AnchorAttempt's errorClass is `RenderContentRefusalError`.
   * ReviewScreen branches on this: `true` → inline title-input +
   * "Titel ändern und neu zeichnen" (Part 3.3 shape); `false` →
   * "Nochmal zeichnen" only, no second "Anderer Titel" button (Part
   * 3.2 shape). Optional at the PWA level for backward compat with
   * pre-slice envelopes.
   */
  readonly contentRefused?: boolean;
}

/**
 * Pick-earlier-attempt slice (Sebastian ruling 2026-09-06) — response
 * from the picker's list endpoints (`GET /book/orders/:id/scenes/:sceneId/attempts`
 * and `/cover/attempts`). Signed URLs are minted on the call, not
 * carried by the envelope. Ordered by `attemptNo asc` so the customer
 * sees his sequence in the order he made it.
 *
 * `activeAttemptId` is the customer's current pick when set, else the
 * newest accepted attempt's id — same "newest wins by default" rule
 * the print picker uses.
 */
export interface AttemptListResponse {
  readonly attempts: ReadonlyArray<{
    readonly attemptId: string;
    readonly attemptNo: number;
    readonly imageUrl: string;
  }>;
  readonly activeAttemptId: string | null;
}

/**
 * PreviewEnvelope — the full response envelope returned by
 * `GET /api/book/orders/:orderId`. Mirrors backend `PreviewEnvelope`
 * in `src/book/preview-read/preview-read.service.ts`.
 *
 * Deliberately status-agnostic: the backend returns this envelope
 * regardless of `BookOrder.status`, so the PWA can ask "where is this
 * order?" before it knows which screen to show (P5-3e ruling
 * 2026-08-03). Downstream step-mapping lives at
 * `pwa/src/features/book/…` in P5-4 Commit B.
 *
 * `zusammenfassung` since 2026-08-05: the Erwartungskarte body at
 * Bau-Spez v3.1 §6 Schritt 3. Empty string when storyText.zusammen-
 * fassung is absent (pre-story-gen books) — same fallback discipline
 * as `title`.
 */
/**
 * Position-5 Commit 6a2 (Sebastian ruling 2026-09-02) — summary-review
 * projection. Populated by the backend envelope only when
 * `status === 'summary_review'`; omitted otherwise. The PWA's
 * SummaryReviewScreen reads all three fields to render attempt
 * indicator, gate self-write mode, and surface the generation-failed
 * banner. The reason column stays server-side and is deliberately not
 * projected.
 */
export interface SummaryReviewProjection {
  readonly runCount: number;
  readonly capReached: boolean;
  readonly generationFailed: boolean;
  /**
   * Position-5 Commit 6b.3 (Sebastian ruling 2026-09-02) — two-boolean
   * derivation of the backend's single `BookOrder.preRunInFlight`
   * enum column. Exactly one of the two is true while a pre-model
   * run is in flight; both are false when no run is in flight.
   *
   * The projection is the source of truth for the retry / self-write
   * loading state — the client's local pending state only covers the
   * enqueue-click round-trip (rendered while the 202 flies), then the
   * envelope polls take over.
   */
  readonly retryInFlight: boolean;
  readonly selfWriteInFlight: boolean;
  /**
   * Paket 1 (Sebastian ruling 2026-09-10) — refusal signal from the
   * V7 pre-model. True when the last pre-model run refused
   * (INHALTSFILTER inappropriate-content or VERSTÄNDLICHKEIT
   * invalid-input). PWA hides the summary block and disables "Passt
   * so" on true; customer edits the brief and hits retry (existing
   * three-run limit + self-write fallback unchanged). The refusal
   * reason string is deliberately not projected — English policy
   * message, log-only backend-side.
   */
  readonly refused: boolean;
  /**
   * Jugendschutz refused-screen hard-stop (Sebastian ruling 2026-09-10
   * late evening). True when, at cap-reached, self-write cannot be
   * offered because the customer's Titelwunsch is still in play AND
   * the last refusal was safety-triggered (`inappropriate_content`).
   * PWA branches at capReached: true → renders the support-line
   * variant (no textarea, no submit); false → self-write panel as
   * today. Backend enforces the same rule as a pre-flight guard in
   * SummaryReviewService.selfWrite (throws self_write_blocked_by_title
   * on a bypass attempt).
   */
  readonly selfWriteBlockedByTitle: boolean;
  /**
   * V7-fatal-on-self-write signal (Sebastian ruling 2026-09-10 late
   * evening). True when the last self-write submission was fatally
   * refused by V7 (kind='inappropriate_content'). PWA renders the
   * released refusal message under the textarea while typed text
   * stays intact in local state. Cleared on the next submit's
   * atomic claim.
   */
  readonly selfWriteRefused: boolean;
  /**
   * V7-fatal-on-self-write cap (Sebastian ruling 2026-09-10 late
   * evening). True when the self-write refusal counter has hit
   * SELF_WRITE_REFUSAL_CAP (2). PWA renders the support-line
   * variant of the panel — no textarea, no submit. Same released
   * copy as the two other cap surfaces (selfWriteBlockedByTitle,
   * release_content_refused_exhausted).
   */
  readonly selfWriteRefusalCapReached: boolean;
  /**
   * Sebastian ruling 2026-09-21 (follow-up 2, Option A) — true iff
   * the customer has already self-edited the summary at least once
   * (any `kind: 'self_write'` entry in the persisted change history).
   * On true the "Oder sag uns, was anders sein soll" change-request
   * link disappears; only further self-edits within the shared
   * 3-attempt budget + "Passt so" remain. The backend rejects a
   * bypass attempt at the retry endpoint with error_code
   * `summary_review_change_request_after_self_write`. Rationale:
   * the retry path re-feeds the ORIGINAL brief as BESCHREIBUNG (V8
   * rule 13 treats it as binding), so events the customer removed
   * via self-write would come back.
   */
  readonly selfWriteHasOccurred: boolean;
}

export interface PreviewEnvelope {
  readonly bookOrderId: string;
  readonly status: BookOrderStatus;
  readonly title: string;
  readonly zusammenfassung: string;
  /**
   * Days remaining until auto-print per Nutzungsbedingungen §3.3
   * (Sebastian ruling 2026-09-13). Slice 2 populates this from
   * `customerReviewReachedAt` — formula: `Math.max(0, 14 −
   * Math.floor((now − customerReviewReachedAt) / DAY_MS))` when the
   * order is in `customer_review` AND the timestamp is set.
   *
   * Value semantics:
   *   - `number` 0..14 — clock is running; the count is the
   *     remainder. Consumed by the urgency line above the change-
   *     scope block on ReviewScreen (rendered when the value is 3
   *     or lower).
   *   - `null` — clock never started (Option A backfill for pre-
   *     migration books, Sebastian ruling 2026-09-13). Sweeper
   *     ignores these books entirely. Urgency line hidden.
   *   - `undefined` — slice 1 state, before the backend ships the
   *     field. Urgency line hidden.
   *
   * The urgency renderer treats all three "no visible countdown"
   * cases (undefined, null, > 3) identically.
   */
  readonly daysUntilAutoprint?: number | null;
  /**
   * Sebastian ruling 2026-09-17 — verification-gate projection. The
   * summary screen disables the release button and renders the
   * verification-pending block when this is `false`. Flips to `true`
   * on the poll after the customer clicks the verify link (server-
   * side truth via `User.emailVerified`). Populated on every status
   * so any downstream mutation surface can render the same block.
   */
  readonly customerEmailVerified: boolean;
  /**
   * Sebastian ruling 2026-09-17 — account email on the current user.
   * Rendered inside the verification-pending block so the customer
   * sees which address he needs to check.
   */
  readonly customerEmail: string;
  readonly summaryReview?: SummaryReviewProjection;
  readonly cover: PreviewCover;
  readonly pages: readonly PreviewPage[];
  /**
   * Sebastian ruling 2026-08-12 — preview-strip tiles. Always carries
   * exactly `k` entries once Scene rows exist (fewer only when N < k,
   * empty pre-extraction). See `PreviewTile` JSDoc.
   */
  readonly previewTiles: readonly PreviewTile[];
  readonly remainingBook: number;
  /**
   * Count behind Bau-Spez v3.1 §6 Schritt 3 Klappzeile "▸ Dinge, die
   * im Buch vorkommen". Distinct across all scenes, filtered on
   * `anchor_required === true` per §B3.3 (generic side-people are
   * never anchored). Zero on pre-extraction books. Same entity in
   * multiple scenes counts once.
   */
  readonly entitiesCount: number;
  /**
   * Block 4 · 4.3 follow-up (Sebastian ruling 2026-09-09) — number of
   * figures on the anchor image, sourced from
   * `BookOrder.intake.cast.length`. Persons and animals count the
   * same (both live in `intake.cast` with a `personType`
   * discriminator; the anchor render draws one figure per entry).
   * Available from status `light_gate_pending` onward, zero on
   * legacy books that predate intake.
   *
   * Consumer: `AnchorReviewScreen` picks
   * `STRINGS.book.anchor.frozenTextSingular` when `figureCount === 1`
   * and `.frozenTextPlural` otherwise.
   */
  readonly figureCount: number;
  /**
   * Scene-text length corridor the server enforces on customer edits.
   * The PWA reads this instead of hardcoding — single source of
   * truth per G7. Values today: `{min: 60, max: 200}` (was
   * `{min: 120, max: 160}` in Block 2 c3 and `{min: 120, max: 450}`
   * pre-c3; widened 2026-08-31 in Block 2 c4 after measurement on
   * 100 scenes across five books — see
   * `book-config.constants.ts::scene_text_min_chars` JSDoc for the
   * measurement + rationale).
   */
  readonly textCorridor: PreviewTextCorridor;
  /**
   * Resizable-panel slice (Sebastian ruling 2026-09-17). Doc-fraction
   * geometry bundle for the approval-screen resize surface. Single-
   * source: PWA reads these instead of hardcoding literals. See
   * backend `PreviewPanelGeometry` for per-field semantics.
   *
   * OPTIONAL on the wire (Sebastian ruling 2026-09-17 fix-4).
   * Cloudflare Pages can serve the new PWA before Railway rolls out
   * the new backend; the old backend's envelope omits this field
   * entirely. Consumers substitute `LEGACY_PANEL_GEOMETRY_FALLBACK`
   * when this field is `undefined` and hide the resize handles.
   */
  readonly panelGeometry?: PreviewPanelGeometry;
  /**
   * 6-3 Bildschirm-5 (Sebastian ruling 2026-08-15). Sales units
   * currently orderable, safe-subset. Empty when no unit is
   * available. `BestellungScreen` renders `FormatCards` from this
   * list and derives the conditional Buchart step from the distinct
   * productClass values (`> 1` → question appears; `=== 1` → skip).
   */
  readonly orderableSalesUnits: readonly OrderableSalesUnit[];
  /**
   * 6-3 Bildschirm-5 (Sebastian ruling 2026-08-15; Austria launch
   * addition 2026-11-02). `shippingCents` keeps the DE launch default
   * so the Format-Tile hint renders a stable value before the customer
   * reaches the Bestellübersicht. `shippingCentsByCountry` exposes the
   * full per-country table from BOOK_CONFIG — the Bestellübersicht
   * picks the entry for the chosen country.
   */
  readonly shippingCents: number;
  readonly shippingCentsByCountry: Readonly<Record<string, number>>;
  /**
   * Stripe rejected/expired-checkout outcome (Sebastian ruling
   * 2026-09-08 late-evening; wire built 2026-09-09, surface deferred).
   *
   * Mirrors `BookOrder.lastCheckoutOutcome` +
   * `lastCheckoutOutcomeAt`. Two webhook branches write these:
   * `checkout.session.async_payment_failed` → 'declined',
   * `checkout.session.expired` → 'expired'. Cleared on the
   * successful `paid` transition.
   *
   * `at` is an ISO-8601 UTC timestamp string. Both fields are null
   * when no bad outcome is on record.
   *
   * ALWAYS projected (not gated on status). The PWA banner-gate
   * (when to display, staleness rules, wording) is Sebastian's
   * decision — the wire ships the raw values.
   *
   * NO customer_cancel value. Customer cancel resets via POST
   * /checkout-cancelled and does not populate these columns.
   */
  readonly lastCheckoutOutcome: 'declined' | 'expired' | null;
  readonly lastCheckoutOutcomeAt: string | null;
  /**
   * Sebastian correction slice 2026-09-10 (Part 1.2) — one-swap-per-
   * book cap flag. Mirrors backend `PreviewEnvelope.photoSwapUsed`.
   * `true` when the customer has already swapped a photo on this book
   * (backend increments `intake.derived.photoSwapCount` on every
   * successful swap regardless of source status). PWA hides both
   * photo-swap surfaces (ImageRefusedScreen swap section + cast list;
   * AnchorReviewScreen "Foto ändern" button) when true. Optional at
   * the PWA level for backward compat with pre-slice envelopes; a
   * missing field defaults to `false` at the consumer.
   */
  readonly photoSwapUsed?: boolean;
  /**
   * Sebastian correction slice 2026-09-10 (Part 2.3) — cast photos
   * for the AnchorReviewScreen voluntary-swap flow. Mirrors backend
   * `PreviewEnvelope.voluntarySwapCastPhotos`. Populated only on
   * `status === 'anchor_review'` when `photoSwapUsed === false`;
   * empty array otherwise. Shape identical to
   * `intake.derived.imageRefusal.castPhotos` — both surfaces share
   * the cast-picker row component.
   */
  readonly voluntarySwapCastPhotos?: readonly EnvelopeImageRefusalCastPhoto[];
  /**
   * Sebastian ruling 2026-09-11 SLICE D — remaining voluntary photo
   * swaps for this book. Distinct from `photoSwapUsed` above (which
   * counts pre-slice recovery swaps only). Cap on the backend is 2;
   * a missing field defaults to 2 for backward compatibility with
   * pre-slice envelopes. `AnchorReviewScreen` renders the count next
   * to the "Foto ändern" button and hides the button when
   * `remaining === 0`.
   */
  readonly voluntaryPhotoSwapsRemaining?: number;
  /**
   * Sebastian ruling 2026-09-11 SLICE D — true iff status is
   * `anchor_pending` AND `intake.derived.replacedPhotoKeys` has at
   * least one entry. `BookRoute` reads this and swaps the WaitScreen
   * heading for the SLICE D "Wir zeichnen die Figur mit dem neuen
   * Foto neu ..." loading line. Optional at the PWA level; a missing
   * field defaults to `false` at the consumer.
   */
  readonly voluntaryPhotoSwapInFlight?: boolean;
  /**
   * Sebastian ruling 2026-09-24 (batch photo-swap slice) — pending
   * voluntary photo swaps that the customer has staged but not yet
   * turned into a redraw. Mirrors backend
   * `PreviewEnvelope.pendingPhotoSwaps`. Populated on
   * `status === 'anchor_review'` from
   * `intake.derived.pendingPhotoSwaps`; empty array on every other
   * status. Optional at the PWA level for backward compat with
   * pre-slice envelopes; a missing field defaults to `[]` at the
   * consumer.
   *
   * Each entry names ONE staged photo (a single cast entry may
   * have BOTH `face` and `fullBody` staged — two entries with the
   * same castKey, different `whichPhoto`).
   */
  readonly pendingPhotoSwaps?: readonly PendingPhotoSwapEntry[];
  /**
   * Intake blob. Optional at the PWA level so pre-migration envelopes
   * still validate. When `status === 'photo_rejected'` the migration
   * populates `intake.derived.rejection` with the failing cast entry
   * details for the PhotoRejectedScreen.
   */
  readonly intake?: EnvelopeIntake;
  /**
   * Freigabe 26-slot expansion (Sebastian ruling 2026-09-07) — the
   * four content fields for the "Über dieses Buch" printed page.
   * Mirrors backend `PreviewEnvelope.aboutBook`. Consumed by
   * `StructurePageView` (about-book variant) on the review screen.
   */
  readonly aboutBook: PreviewAboutBook;
  /**
   * Freigabe 26-slot expansion (Sebastian ruling 2026-09-07) —
   * verbatim projection of the two boot-frozen string bundles the
   * print provider composes on the "Über Dreamable" + "Impressum"
   * pages. Mirrors backend `PreviewEnvelope.structurePages`. The PWA
   * renders these as-is inside `StructurePageView`; no local copy of
   * the strings exists.
   */
  readonly structurePages: PreviewStructurePages;
}

/**
 * OrderableSalesUnit — mirrors backend `OrderableSalesUnit` (safe
 * subset of the config-side `SalesUnit`). Round-1: three entries
 * (three Layflat sizes). Partner + partnerProductId deliberately
 * NOT exposed by the backend; PWA has no legitimate use for them.
 */
export interface OrderableSalesUnit {
  readonly slug: string;
  readonly productClass: 'pappbuch' | 'layflat' | 'hardcover';
  readonly format: string;
  readonly priceCents: number;
}

/**
 * PreviewTextCorridor — mirrors backend `PreviewTextCorridor`.
 */
export interface PreviewTextCorridor {
  readonly min: number;
  readonly max: number;
}

/**
 * PreviewPanelGeometry — mirrors backend `PreviewPanelGeometry` in
 * `src/book/preview-read/preview-read.service.ts`. Resizable-panel
 * slice (Sebastian ruling 2026-09-17). Values are fractions of the
 * interior page's side; the PWA renders each dimension as
 * `value × 100 %`.
 */
export interface PreviewPanelGeometry {
  readonly topMarginFrac: number;
  readonly bottomMarginFrac: number;
  readonly sideMarginFrac: number;
  readonly minWidthFrac: number;
  readonly maxWidthFrac: number;
  readonly maxHeightFrac: number;
  readonly maxCharsScene: number;
  /**
   * Scene-text font size as a fraction of the interior page's side.
   * Consumed by `PanelMoveOverlay` for the text preview inside the
   * drag outline — replaces the pre-slice literals `DOC_SIDE_MM`,
   * `SCENE_TEXT_PT`, `MM_TO_PT`, `DOC_HEIGHT_PT`.
   */
  readonly sceneTextFontFrac: number;
  /**
   * Line-height multiplier the composer applies. Replaces the pre-
   * slice literal `SCENE_TEXT_LEADING_MULT`.
   */
  readonly sceneTextLeadingMult: number;
}

/**
 * Deploy-order fallback (Sebastian ruling 2026-09-17 fix-4).
 * Cloudflare Pages can serve the new PWA before Railway rolls out
 * the new backend; the old backend's envelope has no
 * `panelGeometry` field. When the PWA sees an undefined
 * `envelope.panelGeometry`, it substitutes this constant so the
 * approval screen keeps rendering. Move is preserved with the
 * previous hard-coded frame; the text editor caps at 200 (the pre-
 * slice corridor); the resize handles are HIDDEN by the parent
 * (`resizeEnabled === false` path in `PanelMoveOverlay`).
 *
 * Values mirror the pre-slice literals from the retired
 * `PanelMoveOverlay.tsx` constants (`TOP_MARGIN_FRAC`,
 * `BOTTOM_MARGIN_FRAC`, `SIDE_MARGIN_FRAC`) + the composer's
 * `PANEL_WIDTH_TRIM_FRACTION` for the width cap + the corridor's
 * `scene_text_max_chars = 200` for the character cap.
 */
export const LEGACY_PANEL_GEOMETRY_FALLBACK: PreviewPanelGeometry = {
  topMarginFrac: 6.0 / 216,
  bottomMarginFrac: 14.138 / 216,
  sideMarginFrac: 11.782 / 216,
  minWidthFrac: 0.4,
  maxWidthFrac: 0.55,
  maxHeightFrac: 0.5,
  maxCharsScene: 200,
  sceneTextFontFrac: 13.0 / (216 * (72 / 25.4)),
  sceneTextLeadingMult: 1.4,
};

/**
 * PreviewAboutBook — mirrors backend `PreviewAboutBook` in
 * `src/book/preview-read/preview-read.service.ts`.
 *
 * Freigabe 26-slot expansion (Sebastian ruling 2026-09-07). The
 * printed book's "Über dieses Buch" page composes up to three lines
 * from these four fields (see `pdf-lib-vl21.print-provider.ts:1533-
 * 1560` for the line templates). The PWA's `StructurePageView`
 * `about-book` variant reads this envelope shape verbatim so the
 * review-screen page matches the printed page byte-for-byte on the
 * content axis — no PWA-side derivation, no second copy of the
 * conditional-line rules.
 *
 * Degenerate values reach the wire as '' / null when the intake is
 * incomplete; the PWA renders a shorter page rather than throwing.
 */
export interface PreviewAboutBook {
  readonly creationDateGerman: string;
  readonly childName: string;
  readonly childAgeYears: number | null;
  readonly stadt: string;
}

/**
 * PreviewStructurePages — mirrors backend `PreviewStructurePages` in
 * `src/book/preview-read/preview-read.service.ts`.
 *
 * Freigabe 26-slot expansion (Sebastian ruling 2026-09-07). Verbatim
 * projection of the print provider's two boot-frozen string bundles
 * (`UEBER_DREAMABLE_PLACEHOLDER` + `MANUFACTURER_IMPRINT` at
 * `pdf-lib-vl21.print-provider.ts:246 / :256`). Single source of
 * truth is the print provider; the PWA renders these arrays as-is
 * inside the matching `StructurePageView` variant. Sebastian's
 * ruling: "The PWA reads what the PDF composes." — no PWA-side copy
 * of these strings is permitted by design.
 */
export interface PreviewStructurePages {
  readonly ueberDreamable: readonly string[];
  readonly impressum: readonly string[];
  /**
   * Sebastian ruling 2026-09-16 — back-panel marking block lines
   * (address line 1, address line 2, age line). Mirrors backend
   * `MANUFACTURER_BACK_MARKING`. Same "single source of truth"
   * discipline as `impressum` — one edit to the printer's constant
   * flows to the review screen automatically. Three lines in fixed
   * order: indices 0 + 1 are address lines, index 2 is the age line.
   */
  readonly backMarking: readonly string[];
}

/**
 * EnvelopeRejection — populated when `status === 'photo_rejected'`,
 * absent otherwise. Sits at `envelope.intake.derived.rejection`.
 * Discriminated union on `kind`; `composeCopy` in
 * `PhotoRejectedScreen.tsx` narrows on `kind` to pick the correct
 * STRINGS pair.
 *
 * Two variants today:
 *   - `photo_multi_face` — Google Vision detected > 1 face on the
 *     customer's photo. `faceCount` carries the detection count
 *     (logging + telemetry only; the copy drops the number per
 *     Sebastian ruling 2026-08-06).
 *   - `heic_convert_failed` — heic-convert could not decode the
 *     HEIC / HEIF bytes (Sebastian ruling 2026-08-12 Option 1: this
 *     is customer-actionable — pick another photo — so it belongs
 *     on the photo_rejected screen with its own kind, not in
 *     qa_human_queue). No `faceCount`.
 *
 * Shared fields:
 *   - `castKey` — identifier the swap endpoint
 *     (POST /api/book/orders/:orderId/cast/:castKey/photo) uses.
 *   - `castName` — `{castName}` interpolation source for the copy
 *     (verbatim rule — customer wording lives in the customer-text
 *     file at `docs/geschichtenbuch/P2_Text_und_Prompt_Entscheidungen_
 *     v2_2026-08-05.md` and reaches the customer via STRINGS).
 *   - `whichPhoto` — selects the PATCH body key (`face` → PATCH
 *     `{facePhotoKey}`, `fullBody` → PATCH `{fullBodyPhotoKey}`). NOT
 *     rendered in the customer copy (adjacent rejected-photo tile
 *     shows which photo failed).
 *   - `rejectedPhotoUrl` — signed GCS URL for the failing photo,
 *     1h TTL. `null` when backend signing failed (rare; PWA copy
 *     stands on its own without the tile).
 */
export type EnvelopeRejection =
  | {
      readonly kind: 'photo_multi_face';
      readonly castKey: string;
      readonly castName: string;
      readonly whichPhoto: 'face' | 'fullBody';
      readonly faceCount: number;
      readonly rejectedPhotoUrl: string | null;
    }
  | {
      readonly kind: 'heic_convert_failed';
      readonly castKey: string;
      readonly castName: string;
      readonly whichPhoto: 'face' | 'fullBody';
      readonly rejectedPhotoUrl: string | null;
    };

/**
 * EnvelopeImageRefusal — populated when status is `image_refused`
 * (Sebastian ruling 2026-09-10, Paket 3). Sits at
 * `envelope.intake.derived.imageRefusal`. `stage` selects the
 * customer-facing copy (three stage-specific strings; support line
 * shared).
 *
 *   - `book_anchor` — customer's photos caused the refusal. Only
 *      stage firing today; the book_anchor render carries no
 *      customer text, so the refusal can only be photo-driven.
 *   - `cover` — customer's Titelwunsch caused the refusal
 *      (post-payment cover render, forward-compat).
 *   - `scene` — customer-written scene text caused the refusal
 *      (post-payment scene render, forward-compat).
 *
 * The refusal reason string from OpenAI is NEVER carried on the wire
 * (Sebastian ruling 2026-09-10). Backend logs it, does not display.
 *
 * Paket 3 Item 1 (Sebastian ruling 2026-09-10) — `castPhotos` is
 * populated ONLY on `book_anchor` stage. Carries the customer's cast
 * entries with freshly-signed face-photo URLs (1h TTL) so
 * ImageRefusedScreen can render a photo-swap affordance per entry.
 * Empty array on any cast-shape defect. Never populated on
 * `cover` / `scene` stages (items 2 + 3 wire different affordances).
 */
export interface EnvelopeImageRefusalCastPhoto {
  readonly castKey: string;
  readonly name: string;
  readonly facePhotoUrl: string | null;
}

/**
 * Sebastian ruling 2026-09-24 (batch photo-swap slice) — one staged
 * voluntary photo swap. Mirrors backend
 * `PreviewPendingPhotoSwap` (`preview-read.service.ts`).
 *
 * Sebastian ruling 2026-09-24 (batch photo-swap slice, follow-up
 * fix #1) — the envelope now carries `newPhotoUrl` (freshly-signed
 * URL, 1 h TTL) for the staged photo. PWA uses it as the picker-
 * row thumbnail so the customer sees the NEW photo they just
 * picked instead of the pre-swap face photo. `null` when the
 * backend could not sign (best-effort — PWA falls back to the pre-
 * swap thumbnail).
 */
export interface PendingPhotoSwapEntry {
  readonly castKey: string;
  readonly whichPhoto: 'face' | 'fullBody';
  readonly newPhotoUrl: string | null;
}

export type EnvelopeImageRefusal = {
  readonly stage: 'book_anchor' | 'cover' | 'scene';
  /**
   * Sebastian correction slice 2026-09-10 (Part 2.1) — cause tag
   * selects the PWA copy set + affordance:
   *   - `content_refused` (default when absent): OpenAI's model
   *     refused the input. PWA renders the pre-slice photo-swap flow
   *     on book_anchor stage.
   *   - `technical`: transport / provider terminal failure. PWA
   *     renders the "Nochmal zeichnen" button (POST /anchor-retry).
   *
   * ABSENT means pre-slice book (written before this field was
   * added); the PWA branch treats it as `'content_refused'`.
   */
  readonly cause?: 'content_refused' | 'technical';
  readonly castPhotos?: readonly EnvelopeImageRefusalCastPhoto[];
};

/**
 * EnvelopeIntake — matches the backend intake JSONB blob's shape at
 * the paths the PWA reads. `derived.rejection` is populated only when
 * status is `photo_rejected`; `derived.imageRefusal` is populated
 * only when status is `image_refused` (Sebastian ruling 2026-09-10,
 * Paket 3). Both levels are optional here so an older-shape envelope
 * (before either migration) still compiles and reads cleanly.
 */
/**
 * 2026-10-14 — per-figure block-derivation rejection payload for the
 * `block_derivation_rejected` status. One entry per failed figure,
 * keyed by cast key. Only two reason codes:
 *   - `not_an_animal` — personType='tier' figure whose photo the
 *     vision model could not identify as an animal (e.g. a vehicle).
 *   - `photo_unusable` — personType='person' figure whose face the
 *     vision model could not derive from the photo.
 * The Personen screen reads the matching entry per cast key and
 * renders the hint above that figure. Hint disappears once the
 * figure is swapped or removed (backend clears `rejections` on
 * photo-swap or on the next successful derivation run).
 */
export interface EnvelopeBlockDerivationRejection {
  readonly reason: 'not_an_animal' | 'photo_unusable';
}

/**
 * 2026-10-14 Part 5 — slim cast projection carried on the envelope
 * when `envelope.intake.derived.rejections` is populated. See
 * backend `EnvelopeCastEntry` JSDoc at
 * `src/book/preview-read/preview-read.service.ts` for the three
 * defect classes (name display / Entfernen last-figure guard /
 * tier-vs-person photo-rules) this closes.
 */
export interface EnvelopeCastEntry {
  readonly key: string;
  readonly name: string;
  readonly personType: 'person' | 'tier';
}

export interface EnvelopeIntake {
  readonly derived?: {
    readonly rejection?: EnvelopeRejection;
    readonly imageRefusal?: EnvelopeImageRefusal;
    readonly rejections?: Readonly<Record<string, EnvelopeBlockDerivationRejection>>;
  };
  readonly cast?: readonly EnvelopeCastEntry[];
}

/**
 * BookShellContext — the shape BookShell hands down to every book
 * route via React context (2026-08-07 fix — findings 7 / 10 / 12).
 *
 * ═══ WHY THIS INTERFACE LIVES IN types.ts ═══════════════════════════
 *
 * Before 2026-08-07 this interface sat in BookShell.tsx and
 * BookRoute read it via `useOutletContext<BookShellContext>()`. React
 * Router v7 does NOT propagate outlet context through nested Outlets:
 * BookShell wraps its outlet in a real ProtectedRoute layer, whose
 * bare <Outlet /> at `pwa/src/components/ProtectedRoute.tsx:13`
 * dropped the context on the way down. BookRoute got `undefined`,
 * destructured it, and every book status rendered the top-level
 * Sentry ErrorBoundary fallback instead of its screen. The fix
 * replaces the outlet-context mechanism with a React context (see
 * `pwa/src/features/book/book-shell-context.tsx`).
 *
 * The interface moved here to break the import cycle between
 * `book-shell-context.tsx` (needs the interface for its
 * `createContext<BookShellContext | undefined>`) and `BookShell.tsx`
 * (needs the Provider from `book-shell-context.tsx`). Both files
 * now import BookShellContext from this module; neither imports
 * the other.
 *
 * `refetch` triggers a re-run of the shell's fetch so a mutation
 * downstream (title save, page retry) can request a refresh without
 * duplicating the fetch call.
 */
export interface BookShellContext {
  readonly envelope: PreviewEnvelope | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refetch: () => void;
}
